import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { FileText, Calendar, User, ArrowLeft, X } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const History = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const clientId = params.get('clientId');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState('');

  useEffect(() => {
    const q = clientId ? `?limit=200&client_id=${clientId}` : '?limit=100';
    setLoading(true);
    axios.get(`${API}/history${q}`)
      .then((r) => setHistory(r.data.history || []))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));

    if (clientId) {
      axios.get(`${API}/crm/clients/${clientId}`)
        .then((r) => setClientName(r.data.client?.company?.company_name || ''))
        .catch(() => setClientName(''));
    } else {
      setClientName('');
    }
  }, [clientId]);

  const clearFilter = () => {
    params.delete('clientId');
    setParams(params);
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="bg-white rounded-2xl border border-gray-100 p-8 h-96 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="history-page">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          {clientId && (
            <button
              data-testid="back-to-client"
              onClick={() => navigate(`/clients/${clientId}`)}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div>
            <h1 className="text-4xl font-semibold text-gray-900 tracking-tight">Generation History</h1>
            <p className="text-gray-500 mt-1">
              {clientId
                ? <>Showing generations for <span className="font-medium text-gray-700">{clientName || clientId}</span></>
                : 'Complete history across all clients'}
            </p>
          </div>
        </div>
        {clientId && (
          <button
            data-testid="clear-filter"
            onClick={clearFilter}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700"
          >
            <X className="w-3.5 h-3.5" /> Clear filter
          </button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-200">
        <div className="p-6 border-b border-gray-200 grid grid-cols-4 gap-4 text-xs font-semibold tracking-wide text-gray-500 ">
          <div>Client</div>
          <div>Documents</div>
          <div>Date</div>
          <div>Count</div>
        </div>

        <ScrollArea className="h-[600px]">
          <div className="divide-y divide-gray-100">
            {history.length > 0 ? (
              history.map((item, index) => (
                <div key={item.id || index} data-testid={`history-item-${index}`} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="grid grid-cols-4 gap-4 items-start">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">{item.client_name}</div>
                        {item.source === 'generate_pending' && (
                          <div className="text-xs text-emerald-600 font-medium">auto-drafted</div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <FileText className="w-4 h-4 text-gray-500 mt-1" />
                      <div className="text-sm text-gray-600">{item.template_names?.join(', ') || 'N/A'}</div>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4" />
                      {item.generated_at ? format(new Date(item.generated_at), 'MMM dd, yyyy HH:mm') : 'N/A'}
                    </div>

                    <div className="text-sm font-medium text-gray-900">
                      {item.template_ids?.length || 0} documents
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-12 text-center text-gray-500">
                {clientId
                  ? 'No generations yet for this client.'
                  : 'No history available yet. Start generating documents!'}
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default History;
